import type { z } from "zod";
import {
  AUTH_ROUTES,
  ERROR_CODES,
  refreshResponseSchema,
  type ApiResponse,
  type AuthSession,
} from "@nh/shared";
import { ApiError, apiFetch, isApiError, type ApiFetchOptions } from "./client";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Sesión del frontend
 *
 * El ACCESS TOKEN vive aquí, en una variable de módulo. NUNCA en
 * localStorage ni sessionStorage: cualquier XSS puede leer el storage,
 * y un token robado de ahí sobrevive al cierre de la pestaña. En memoria
 * muere con la pestaña, y para robarlo hay que ejecutar código en el
 * mismo momento en que la sesión está viva.
 *
 * El REFRESH TOKEN no aparece por ningún lado de este archivo: viaja en
 * una cookie httpOnly que pone el backend y que JavaScript no puede leer.
 * Por eso todas las peticiones van con `credentials: "include"`.
 *
 * `AUTH_ROUTES` ya trae el prefijo `/api`, así que estas llamadas pasan
 * `baseUrl: ""` — si no, quedaría `/api/api/auth/...`.
 * ═══════════════════════════════════════════════════════════════════
 */

const NO_PREFIX = { baseUrl: "" } as const;

let accessToken: string | null = null;

/** Suscriptores a "la sesión se acabó" (token inválido o logout). */
const clearedListeners = new Set<() => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Borra el token y avisa. Lo llama el propio cliente cuando el servidor
 * dice `TOKEN_INVALID`, para que el contexto de auth mande a la persona
 * a la pantalla de acceso sin que nadie tenga que acordarse de hacerlo.
 */
export function clearSession(): void {
  accessToken = null;
  refreshInFlight = null;
  for (const listener of clearedListeners) listener();
}

export function onSessionCleared(listener: () => void): () => void {
  clearedListeners.add(listener);
  return () => clearedListeners.delete(listener);
}

/**
 * La renovación EN VUELO. Es lo que evita la estampida: si cinco
 * peticiones expiran a la vez, las cinco esperan esta misma promesa y el
 * backend ve UNA sola llamada a /auth/refresh. Sin esto, cinco refresh
 * simultáneos con rotación de refresh token se invalidan entre ellos y
 * la sesión se cae sola.
 */
let refreshInFlight: Promise<AuthSession> | null = null;

export function refreshSession(): Promise<AuthSession> {
  if (!refreshInFlight) {
    refreshInFlight = apiFetch(AUTH_ROUTES.refresh, refreshResponseSchema, {
      ...NO_PREFIX,
      method: "POST",
    })
      .then((session) => {
        accessToken = session.accessToken;
        return session;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * Fetch autenticado.
 *
 * 1. Adjunta el access token si lo hay.
 * 2. Ante `TOKEN_EXPIRED`, renueva UNA vez (compartiendo la renovación en
 *    vuelo) y reintenta la petición UNA sola vez. Si el reintento vuelve a
 *    expirar, se rinde: reintentar en bucle solo alarga el fallo.
 * 3. Ante `TOKEN_INVALID`, cierra la sesión — ese token no se arregla
 *    renovando: fue manipulado, revocado o firmado con otra llave.
 */
export async function authFetch<TSchema extends z.ZodType<ApiResponse<unknown>>>(
  path: string,
  schema: TSchema,
  options: ApiFetchOptions = {},
): Promise<Extract<z.infer<TSchema>, { ok: true }>["data"]> {
  try {
    return await withToken(path, schema, options);
  } catch (error) {
    if (!isApiError(error)) throw error;

    if (error.code === ERROR_CODES.TOKEN_INVALID) {
      clearSession();
      throw error;
    }

    if (error.code !== ERROR_CODES.TOKEN_EXPIRED) throw error;

    try {
      await refreshSession();
    } catch (refreshError) {
      // No se pudo renovar: la sesión larga también murió.
      clearSession();
      throw isApiError(refreshError)
        ? refreshError
        : new ApiError({
            code: ERROR_CODES.UNAUTHENTICATED,
            message: "Tu sesión expiró. Vuelve a entrar.",
            cause: refreshError,
          });
    }

    return await withToken(path, schema, options);
  }
}

function withToken<TSchema extends z.ZodType<ApiResponse<unknown>>>(
  path: string,
  schema: TSchema,
  options: ApiFetchOptions,
): Promise<Extract<z.infer<TSchema>, { ok: true }>["data"]> {
  return apiFetch(path, schema, {
    ...options,
    headers: {
      ...options.headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
}

/** Helper para las rutas de `AUTH_ROUTES`, que ya traen `/api`. */
export function authRouteFetch<TSchema extends z.ZodType<ApiResponse<unknown>>>(
  path: string,
  schema: TSchema,
  options: ApiFetchOptions = {},
): Promise<Extract<z.infer<TSchema>, { ok: true }>["data"]> {
  return authFetch(path, schema, { ...NO_PREFIX, ...options });
}

/** Solo para pruebas: deja el módulo como recién cargado. */
export function __resetSessionForTests(): void {
  accessToken = null;
  refreshInFlight = null;
  clearedListeners.clear();
}
